import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Tv, Plus, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { launchCard } from '../lib/launch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  netflix:        { name: 'Netflix',      color: '#E50914' },
  hulu:           { name: 'Hulu',         color: '#1CE783' },
  disney_plus:    { name: 'Disney+',      color: '#113CCF' },
  hbo_max:        { name: 'HBO Max',      color: '#B535F6' },
  amazon_prime:   { name: 'Amazon Prime', color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',    color: '#555' },
  peacock:        { name: 'Peacock',      color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',   color: '#0064FF' },
};

const MOODS = [
  { id: 'stressed',      emoji: '😤', label: 'Stressed',      desc: 'Light & easy watching' },
  { id: 'happy',         emoji: '😄', label: 'Happy',         desc: 'Fun & uplifting shows' },
  { id: 'bored',         emoji: '😑', label: 'Bored',         desc: 'Action-packed & thrilling' },
  { id: 'adventurous',   emoji: '🤠', label: 'Adventurous',   desc: 'Epic & exciting journeys' },
  { id: 'romantic',      emoji: '🥰', label: 'Romantic',      desc: 'Love stories & dramas' },
  { id: 'sad',           emoji: '😢', label: 'Sad',           desc: 'Lean in & feel it' },
  { id: 'nostalgic',     emoji: '🕰️', label: 'Nostalgic',     desc: 'Comforting throwbacks' },
  { id: 'escapist',      emoji: '🚀', label: 'Escapist',      desc: 'Worlds far from this one' },
  { id: 'contemplative', emoji: '🤔', label: 'Contemplative', desc: 'Slow burns & big ideas' },
  { id: 'energetic',     emoji: '⚡', label: 'Energetic',     desc: 'High-octane & relentless' },
  { id: 'spooky',        emoji: '👻', label: 'Spooky',        desc: 'Scares & slow dread' },
  { id: 'funny',         emoji: '🤣', label: 'Funny',         desc: 'Laugh-out-loud picks' },
];

export default function Mood() {
  const { user, getToken } = useAuth();
  const [selectedMood, setSelectedMood] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingShow, setAddingShow] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [selectedService, setSelectedService] = useState('');
  const [connectedServices, setConnectedServices] = useState([]);
  const [playNowShow, setPlayNowShow] = useState(null);

  // Ref to the results section so we can scroll to it after a mood is picked
  const resultsRef = useRef(null);

  // Scroll into view when results land OR when loading starts (so the spinner is visible)
  useEffect(() => {
    if ((loading || results.length > 0) && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, results]);

  const fetchMoodQueue = async (mood) => {
    setSelectedMood(mood);
    setLoading(true);
    setResults([]);
    try {
      const token = await getToken();
      const res = await axios.post(
        `${API}/mood/queue`,
        { mood },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(res.data.results || []);
      const svcRes = await axios.get(`${API}/services/connected`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConnectedServices(svcRes.data || []);
    } catch (err) {
      toast.error('Failed to load mood queue');
    } finally {
      setLoading(false);
    }
  };

  // One click when MoodQ already knows the service the title streams on
  // (the normal case); otherwise the dialog asks which service.
  const openAddDialog = (show) => {
    if (show.service) {
      addToWatchlist(show, show.service, false);
      return;
    }
    setSelectedShow(show);
    setSelectedService(connectedServices[0] || '');
    setDialogOpen(true);
    setPlayNowShow(null);
  };

  const addToWatchlist = async (show = selectedShow, serviceId = selectedService, fromDialog = true) => {
    if (!show || !serviceId) return;
    setAddingShow(show.tmdb_id);
    try {
      const token = await getToken();
      await axios.post(
        `${API}/shows`,
        {
          title: show.title,
          service_id: serviceId,
          image_url: show.poster_url,
          tmdb_id: String(show.tmdb_id),
          media_type: show.media_type || 'tv',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${show.title} added to watchlist!`);
      logRecEvent('watchlisted', show);
      if (fromDialog) setPlayNowShow(show);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add show');
    } finally {
      setAddingShow(null);
    }
  };

  // Client-reported half of backend/recommendation_events.py's feedback
  // ledger -- "shown" is logged server-side by get_mood_queue itself (it's
  // the only one who knows what was actually returned); this fills in the
  // half only the client can see. Fire-and-forget, same contract as the
  // mobile app's identical call: a logging failure must never block the
  // real action (playing/adding a show).
  const logRecEvent = async (eventType, show) => {
    try {
      const token = await getToken();
      await axios.post(
        `${API}/recommendations/event`,
        { surface: 'mood_queue', event_type: eventType, tmdb_id: String(show.tmdb_id), title: show.title, context: { mood: selectedMood } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // best-effort only
    }
  };

  const handlePlay = (show) => {
    // Continuing an in-progress title isn't a MoodQ recommendation working.
    if (!show.in_progress) logRecEvent('clicked', show);
    launchCard(show, getToken);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>
  MoodQ
</h1>
<p className="text-neutral-500 mt-1">Pick your mood and we'll build your perfect queue.</p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 sm:gap-2">
        {MOODS.map(mood => (
          <button
            key={mood.id}
            onClick={() => fetchMoodQueue(mood.id)}
            className={`flex flex-col items-center justify-center gap-1.5 py-4 px-1 rounded-xl border transition-all ${
              selectedMood === mood.id
                ? 'bg-violet-600/20 border-violet-500/50 text-white'
                : 'bg-[#0A0A0A] border-white/5 hover:border-white/15 text-neutral-300'
            }`}
          >
            <span className="text-5xl leading-none">{mood.emoji}</span>
            <span className="text-xs font-semibold">{mood.label}</span>
          </button>
        ))}
      </div>

      {/* Anchor target for the auto-scroll. Wraps loading + results + empty state */}
      <div ref={resultsRef} className="scroll-mt-4">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && results.length > 0 && (
          <div>
            <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              {MOODS.find(m => m.id === selectedMood)?.emoji} {MOODS.find(m => m.id === selectedMood)?.label} picks for you
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {results.map(show => (
                <div key={show.tmdb_id} className="group relative bg-[#0A0A0A] border border-white/5 rounded-xl overflow-hidden hover:border-white/15 transition-all">
                  <div className="relative aspect-[2/3] bg-white/5 overflow-hidden">
                    {show.service && SERVICE_META[show.service] && (
                      <span
                        className="absolute top-2 right-2 z-10 px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                        style={{ backgroundColor: SERVICE_META[show.service].color }}
                      >
                        {SERVICE_META[show.service].name}
                      </span>
                    )}
                    {show.poster_url
                      ? <img src={show.poster_url} alt={show.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      : <div className="w-full h-full flex items-center justify-center"><Tv className="w-8 h-8 text-neutral-600" /></div>
                    }
                  </div>
                  <div className="p-3">
                    {show.reason && <p className="text-[10px] font-semibold text-violet-400 uppercase tracking-wide truncate mb-0.5">{show.reason}</p>}
                    <p className="text-sm font-semibold truncate">{show.title}</p>
                    {(show.rating > 0 || show.media_type === 'movie') && (
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {[show.rating > 0 ? `⭐ ${show.rating}` : null, show.media_type === 'movie' ? 'Film' : null].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {show.overview && <p className="text-xs text-neutral-600 mt-1 line-clamp-2">{show.overview}</p>}
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => handlePlay(show)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-neutral-200 text-xs font-semibold transition-all"
                      >
                        <Play className="w-3 h-3" fill="currentColor" />
                        {show.in_progress ? 'Continue ▶' : 'Play ▶'}
                      </button>
                      {!show.in_progress && (
                        <button
                          onClick={() => openAddDialog(show)}
                          disabled={addingShow === show.tmdb_id}
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-violet-600/20 hover:bg-violet-600/40 text-violet-400 text-xs font-semibold transition-all"
                        >
                          <Plus className="w-3 h-3" />
                          Watchlist
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && selectedMood && results.length === 0 && (
          <div className="text-center py-16">
            <p className="text-neutral-500">Nothing on your services fits this mood right now. Try another mood, or connect more services in Settings.</p>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setPlayNowShow(null); }}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Add to Watchlist</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {selectedShow && (
              <div className="flex items-center gap-3">
                {selectedShow.poster_url && <img src={selectedShow.poster_url} alt={selectedShow.title} className="w-10 h-14 rounded-lg object-cover" />}
                <p className="text-sm font-semibold">{selectedShow.title}</p>
              </div>
            )}

            {/* Play now prompt shown after adding */}
            {playNowShow ? (
              <div className="space-y-3">
                <p className="text-sm text-neutral-300 text-center">Added! Want to play it now?</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => { handlePlay(playNowShow); setDialogOpen(false); setPlayNowShow(null); }}
                    className="flex-1 bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold hover:opacity-90 gap-2"
                  >
                    <Play className="w-4 h-4" fill="white" /> Play Now
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setDialogOpen(false); setPlayNowShow(null); }}
                    className="flex-1 border-white/10 text-neutral-300 rounded-full hover:bg-white/5"
                  >
                    Later
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-white rounded-xl h-12">
                    <SelectValue placeholder="Select streaming service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 text-white">
                    {connectedServices.map(svcId => (
                      <SelectItem key={svcId} value={svcId}>{SERVICE_META[svcId]?.name || svcId}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => addToWatchlist()}
                  disabled={!selectedService || addingShow != null}
                  className="w-full bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold hover:opacity-90"
                >
                  {addingShow != null ? 'Adding…' : 'Add to Watchlist'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
